import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountsAPI, unwrapVendors, extractSourceMeta } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES } from '../lib/period';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Building2, Search, ChevronRight, Database
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const VendorsPage = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [sourceMeta, setSourceMeta] = useState({ state: 'VALID', source: null, reason_human: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const periodState = usePeriod(PERIOD_MODES.RANGE_ALL_TIME);
  const [resolvedPeriod, setResolvedPeriod] = useState(null);

  const loadVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.assign(params, periodState.apiParams);
      const response = await accountsAPI.getVendors(params);
      setResolvedPeriod(response.data);
      // Truth Contract: backend may return either a flat array (legacy) or
      // an envelope `{state, source, vendors:[...]}`. Always normalize to
      // an array so the UI never crashes on `.filter()` / `.reduce()`.
      setVendors(unwrapVendors(response.data));
      setSourceMeta(extractSourceMeta(response.data));
    } catch (err) {
      // Detail is auto-coerced to a string by the response interceptor in api.js.
      toast.error(err.response?.data?.detail || "Operation failed");
      setVendors([]);
      setSourceMeta({ state: 'PARTIAL', source: 'EXPENSE_CAPTURE_UNAVAILABLE', reason_human: 'Vendor list could not be loaded.' });
    }
    setLoading(false);
  }, [periodState.apiParams]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // Legacy `handleSync` was wired to POST /api/sync/all — permanently
  // decommissioned (returns 410 Gone). Vendors are now read-through-proxied
  // from Expense Capture; there is nothing for the UI to "sync". The button
  // and its error-handling state have been removed entirely.

  // Empty / whitespace-only search bypasses filtering entirely.
  // Without this guard, `v.name?.toLowerCase().includes("")` was
  // returning `undefined` for any field that didn't exist on the
  // legacy shape and the OR-chain went falsy — wiping the table even
  // when the array was populated. The actual canonical EC fields are
  // exposed as `name`/`gstin`/`category` aliases by `normalizeVendor`
  // in `lib/api.js`, so this filter just needs to be value-aware.
  const q = search.trim().toLowerCase();
  const filteredVendors = q
    ? vendors.filter(v =>
        (v.name || '').toLowerCase().includes(q) ||
        (v.gstin || '').toLowerCase().includes(q) ||
        (v.category || '').toLowerCase().includes(q)
      )
    : vendors;

  const totalOutstanding = vendors.reduce((sum, v) => sum + (v.outstanding_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="vendors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-manrope font-bold tracking-tight">Vendors</h1>
          <p className="text-muted-foreground mt-1">
            Manage vendors and track outstanding amounts
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Total Outstanding</p>
            <p className="text-xl font-mono font-bold text-amber-400">{formatCurrency(totalOutstanding)}</p>
          </div>
          <Button 
            variant="outline" 
            disabled
            data-testid="vendors-source-badge"
            title="Bills/vendors are fetched live from Expense Capture — no sync action required."
          >
            <Database className="w-4 h-4 mr-2" />
            Bills: Expense Capture · Live read
          </Button>
        </div>
      </div>

      {/* Source explainer (replaces the legacy Sync Result card) */}
      <Card className="border border-zinc-700/40 bg-zinc-900/30" data-testid="vendors-source-info">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 text-[11px] text-zinc-400">
            <Database className="w-3.5 h-3.5 text-emerald-400" />
            <span>
              Vendors and bills shown here are read live from{" "}
              <span className="text-emerald-300 font-medium">Expense Capture</span>.
              No local mirror is maintained. To refresh, simply reload this page.
            </span>
          </div>
        </CardContent>
      </Card>

      {/* PARTIAL / UNAVAILABLE banner — Truth Contract: never silently
          show "no vendors" when the upstream EC source is unreachable. */}
      {sourceMeta.state === 'PARTIAL' && (
        <Card
          className="border border-amber-500/40 bg-amber-500/5"
          data-testid="vendors-partial-banner"
        >
          <CardContent className="p-3">
            <div className="flex items-start gap-2 text-[12px] text-amber-300">
              <Database className="w-3.5 h-3.5 mt-0.5 text-amber-400 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-300">Vendor list unavailable</p>
                <p className="text-amber-200/80 mt-0.5">
                  {sourceMeta.reason_human || 'Expense Capture is currently unreachable. The list below may be empty — that is not a confirmed zero.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Date Filter */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search vendors by name, GSTIN, or category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-background/50"
                data-testid="vendor-search"
              />
            </div>
            <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE_ALL_TIME} resolved={resolvedPeriod} />
          </div>
        </CardContent>
      </Card>

      {/* Vendors Table */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={`skeleton-${i}`} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="font-manrope">Vendor</TableHead>
                  <TableHead className="font-manrope">GSTIN</TableHead>
                  <TableHead className="font-manrope">Category</TableHead>
                  <TableHead className="font-manrope text-right">Bills</TableHead>
                  <TableHead className="font-manrope text-right">Total Billed</TableHead>
                  <TableHead className="font-manrope text-right">Outstanding</TableHead>
                  <TableHead className="font-manrope w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.map((vendor) => (
                  <TableRow 
                    key={vendor.vendor_id} 
                    className="border-border/50 hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigate(`/vendors/${vendor.vendor_id}`)}
                    data-testid={`vendor-row-${vendor.vendor_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="font-medium">{vendor.name}</p>
                          {vendor.phone && (
                            <p className="text-xs text-muted-foreground">{vendor.phone}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{vendor.gstin || '-'}</TableCell>
                    <TableCell>
                      {vendor.category && (
                        <Badge variant="outline">{vendor.category}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">{vendor.bill_count || 0}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(vendor.total_billed)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono font-semibold ${vendor.outstanding_amount > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {formatCurrency(vendor.outstanding_amount)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
                {filteredVendors.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {vendors.length === 0 ? (
                        <div className="space-y-2">
                          <p>No vendors found</p>
                          <p className="text-sm">Vendor data is read live from Expense Capture — reload the page to refresh.</p>
                        </div>
                      ) : (
                        'No vendors match your search'
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorsPage;
